import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Box, Button, Popover, Stack } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { StyledMenu } from '@ui/components/common/Menu';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
export const ButtonDropdown = ({ label, startIcon, options, selectedItem, onSelectionChange, onDateRangeChange, customDateRange, variant = 'outlined', color = 'inherit', size = 'medium', enableDateRange = false, displayWithIdForLabel = false, keepOriginalOrder = false, ...buttonProps }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentDateSelection, setCurrentDateSelection] = useState([null, null]);
    // In-dropdown range panel
    const [isRangePanelOpen, setIsRangePanelOpen] = useState(false);
    const [startTemp, setStartTemp] = useState(null);
    const [endTemp, setEndTemp] = useState(null);
    const buttonRef = React.useRef(null);
    const isOpen = Boolean(anchorEl);
    // No inline calendar positioning needed with panel
    const getDateValues = () => {
        if (customDateRange?.startDate &&
            customDateRange?.endDate &&
            customDateRange.startDate !== '' &&
            customDateRange.endDate !== '') {
            try {
                const start = dayjs(customDateRange.startDate);
                const end = dayjs(customDateRange.endDate);
                if (start.isValid() && end.isValid()) {
                    return [start, end];
                }
            }
            catch {
                // Invalid dates, return null
            }
        }
        return [null, null];
    };
    // Use first option as default if no selection is made
    const defaultOption = options[0];
    const effectiveSelectedItem = selectedItem || defaultOption?.id;
    // Reorder options so selected item appears first
    const reorderedOptions = keepOriginalOrder
        ? options
        : effectiveSelectedItem
            ? (() => {
                const selectedOption = options.find((option) => option.id === effectiveSelectedItem);
                const otherOptions = options.filter((option) => option.id !== effectiveSelectedItem);
                return selectedOption ? [selectedOption, ...otherOptions] : options;
            })()
            : options;
    // Find the selected option for display (use first option if none selected)
    const selectedOption = effectiveSelectedItem
        ? options.find((option) => option.id === effectiveSelectedItem)
        : defaultOption;
    // Format date range for display
    const formatDateRange = (range) => {
        if (!range?.startDate || !range?.endDate)
            return 'Custom';
        try {
            const start = new Date(range.startDate);
            const end = new Date(range.endDate);
            return `${start.getDate()}/${start.getMonth() + 1}/${start.getFullYear()} - ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`;
        }
        catch {
            return 'Custom';
        }
    };
    // Determine display label - show date range if custom is selected
    let displayLabel = label;
    if (enableDateRange && effectiveSelectedItem === 'custom') {
        if (customDateRange?.startDate && customDateRange?.endDate) {
            displayLabel = formatDateRange(customDateRange);
        }
        else if (currentDateSelection[0]) {
            try {
                const start = currentDateSelection[0];
                const d = start.toDate();
                displayLabel = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} - ...`;
            }
            catch {
                displayLabel = selectedOption?.text || label;
            }
        }
        else {
            displayLabel = selectedOption?.text || label;
        }
    }
    else if (selectedOption) {
        displayLabel =
            displayWithIdForLabel && effectiveSelectedItem
                ? effectiveSelectedItem.toUpperCase()
                : selectedOption.text;
    }
    const displayIcon = selectedOption?.icon ?? startIcon;
    const handleButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleMenuItemSelect = (itemId) => {
        if (enableDateRange && itemId === 'custom') {
            // Close the menu and open separate start/end pickers
            setAnchorEl(null);
            // Immediately set selection to 'custom' so the button shows Date range
            onSelectionChange?.('custom');
            // Default range: start of today to end of today
            const defaultStart = dayjs().startOf('day');
            const defaultEnd = dayjs().endOf('day');
            setStartTemp(defaultStart);
            setEndTemp(defaultEnd);
            setCurrentDateSelection([defaultStart, defaultEnd]);
            onDateRangeChange?.({
                startDate: defaultStart.toISOString(),
                endDate: defaultEnd.toISOString()
            });
            setTimeout(() => {
                setIsRangePanelOpen(true);
            }, 150);
            return;
        }
        onSelectionChange?.(itemId);
        setAnchorEl(null);
    };
    const handleStartPicked = (val) => {
        if (!val)
            return;
        const end = endTemp || (customDateRange?.endDate ? dayjs(customDateRange.endDate) : null);
        setStartTemp(val);
        setCurrentDateSelection(([_, prevEnd]) => [val, prevEnd]);
        onDateRangeChange?.({
            startDate: val.toISOString(),
            endDate: (end || val.endOf('day')).toISOString()
        });
    };
    const handleEndPicked = (val) => {
        if (!val)
            return;
        const start = startTemp ||
            (customDateRange?.startDate ? dayjs(customDateRange.startDate) : null) ||
            dayjs().startOf('day');
        setEndTemp(val);
        setCurrentDateSelection(([prevStart]) => [prevStart, val]);
        onDateRangeChange?.({
            startDate: start.toISOString(),
            endDate: val.toISOString()
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx(Button, { ...buttonProps, ref: buttonRef, variant: variant, color: color, size: size, startIcon: displayIcon, endIcon: _jsx(KeyboardArrowDown, { sx: {
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                    } }), onClick: handleButtonClick, children: displayLabel }), _jsx(StyledMenu, { anchorEl: anchorEl, open: isOpen, onClose: handleMenuClose, actions: reorderedOptions, selectedItem: selectedItem, setSelectedItem: (id) => handleMenuItemSelect(id), anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                } }), enableDateRange && (_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsx(Popover, { open: isRangePanelOpen, anchorEl: buttonRef.current, onClose: () => setIsRangePanelOpen(false), anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, slotProps: { paper: { sx: { p: 2 } } }, children: _jsx(Box, { children: _jsxs(Stack, { direction: 'row', spacing: 2, children: [_jsx(DatePicker, { value: startTemp || getDateValues()[0] || dayjs().startOf('day'), onChange: handleStartPicked, slotProps: { textField: { label: t('labels.startDate'), size: 'small' } } }), _jsx(DatePicker, { value: endTemp || getDateValues()[1] || dayjs().endOf('day'), onChange: handleEndPicked, minDate: startTemp || getDateValues()[0] || undefined, slotProps: { textField: { label: t('labels.endDate'), size: 'small' } } })] }) }) }) }))] }));
};
export default ButtonDropdown;
